import { useEffect, useRef, useState, useCallback } from 'react'
import { trustpilotReviews, TrustpilotReview as ReviewType } from '../data/reviews'

interface TrustpilotReviewProps {
  reviews?: ReviewType[];
  autoScrollInterval?: number;
}

const TrustpilotReviewComponent = ({
  reviews = trustpilotReviews,
  autoScrollInterval = 5000
}: TrustpilotReviewProps) => {
  const miniWidgetRef = useRef<HTMLDivElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined)
  const [isAutoScrolling, setIsAutoScrolling] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [startX, setStartX] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Trustpilot Business Unit ID
  const businessUnitId = '677ae66981011505a3a2367f'
  const trustpilotUrl = 'https://www.trustpilot.com/review/gaiapowered.co.uk'

  const goToIndex = useCallback((index: number) => {
    const newIndex = Math.max(0, Math.min(index, reviews.length - 1))
    setCurrentIndex(newIndex)
  }, [reviews.length])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length)
  }, [reviews.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length)
  }, [reviews.length])

  const handleManualNavigation = (direction: 'prev' | 'next') => {
    setIsAutoScrolling(false)
    if (direction === 'prev') {
      goToPrevious()
    } else {
      goToNext()
    }
  }

  const handleDotClick = (index: number) => {
    if (index === currentIndex) return
    setIsAutoScrolling(false)
    goToIndex(index)
  }

  // Touch/Mouse drag handlers
  const handleDragStart = (clientX: number) => {
    setIsAutoScrolling(false)
    setIsDragging(true)
    setStartX(clientX)
    setDragOffset(0)
  }

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return
    const diff = clientX - startX
    setDragOffset(diff)
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)

    const threshold = 50 // minimum drag distance to trigger navigation

    if (dragOffset > threshold) {
      // Dragged right - go to previous
      goToPrevious()
    } else if (dragOffset < -threshold) {
      // Dragged left - go to next
      goToNext()
    }

    setDragOffset(0)
  }

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    handleDragStart(e.clientX)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    handleDragMove(e.clientX)
  }

  const handleMouseUp = () => {
    handleDragEnd()
  }

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd()
    }
  }

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientX)
  }

  const handleTouchEnd = () => {
    handleDragEnd()
  }

  // Sync container height to current card
  useEffect(() => {
    const updateHeight = () => {
      const card = cardRefs.current[currentIndex]
      if (card) setContainerHeight(card.offsetHeight)
    }
    updateHeight()
    window.addEventListener('resize', updateHeight)
    return () => window.removeEventListener('resize', updateHeight)
  }, [currentIndex])

  // Auto-scroll effect
  useEffect(() => {
    if (isAutoScrolling && reviews.length > 1) {
      timerRef.current = setInterval(goToNext, autoScrollInterval)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isAutoScrolling, autoScrollInterval, goToNext, reviews.length])

  useEffect(() => {
    // Load Trustpilot widget script if not already loaded
    if (!document.querySelector('script[src*="trustpilot"]')) {
      const script = document.createElement('script')
      script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
      script.async = true
      document.body.appendChild(script)

      script.onload = () => {
        if (window.Trustpilot && miniWidgetRef.current) {
          window.Trustpilot.loadFromElement(miniWidgetRef.current, true)
        }
      }
    } else {
      // Script already loaded, just initialize widget
      if (window.Trustpilot && miniWidgetRef.current) {
        window.Trustpilot.loadFromElement(miniWidgetRef.current, true)
      }
    }
  }, [])

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'text-[#00B67A]' : 'text-gray-300'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  const renderReviewCard = (review: ReviewType, index: number) => (
    <div
      key={index}
      ref={(el) => { cardRefs.current[index] = el }}
      className="w-full flex-shrink-0 px-1"
    >
      <div className="flex bg-gray-50 rounded-xl p-6 md:p-8 shadow-sm border border-gray-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-primary-green rounded-full flex items-center justify-center text-white font-bold text-lg">
              {review.customerName.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="flex-grow min-w-0">
            <div className="flex items-center justify-between mb-2 flex gap-2">
              <h4 className="font-semibold text-lg text-dark-plum">
                {review.customerName}
              </h4>
              {renderStars(review.rating)}
            </div>
            <p className="text-gray-700 leading-relaxed italic mb-3">
              "{review.comment}"
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
              {review.verified && (
                <>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-[#00B67A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Verified Customer</span>
                  </div>
                  <span>•</span>
                </>
              )}
              <span>{review.date}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // Calculate transform based on current index and drag offset
  const getTransformStyle = () => {
    const baseOffset = -currentIndex * 100
    const dragPercentage = isDragging ? (dragOffset / (carouselRef.current?.offsetWidth || 1)) * 100 : 0
    return {
      transform: `translateX(calc(${baseOffset}% + ${dragPercentage}%))`,
      transition: isDragging ? 'none' : 'transform 400ms ease-out'
    }
  }

  return (
    <section className="py-16 bg-white">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Section Header with Live Trustpilot Rating */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-poppins font-bold text-dark-plum mb-6">
              Trusted by Our Customers
            </h2>

            {/* Trustpilot Mini Widget - Live Rating */}
            <div className="flex justify-center mb-4">
              <div
                ref={miniWidgetRef}
                className="trustpilot-widget"
                data-locale="en-GB"
                data-template-id="5419b6a8b0d04a076446a9ad"
                data-businessunit-id={businessUnitId}
                data-style-height="24px"
                data-style-width="100%"
                data-theme="light"
              >
                <a href={trustpilotUrl} target="_blank" rel="noopener noreferrer">
                  Trustpilot
                </a>
              </div>
            </div>
          </div>

          {/* Review Carousel */}
          <div className="relative">
            {/* Navigation Arrows - hidden on mobile, visible on md and up */}
            {reviews.length > 1 && (
              <>
                <button
                  onClick={() => handleManualNavigation('prev')}
                  className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 w-10 h-10 bg-white rounded-full shadow-md items-center justify-center text-gray-600 hover:text-primary-green hover:shadow-lg transition-all z-20"
                  aria-label="Previous review"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleManualNavigation('next')}
                  className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 w-10 h-10 bg-white rounded-full shadow-md items-center justify-center text-gray-600 hover:text-primary-green hover:shadow-lg transition-all z-20"
                  aria-label="Next review"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Horizontal Carousel Container */}
            <div
              ref={carouselRef}
              className="overflow-hidden cursor-grab active:cursor-grabbing transition-[height] duration-400 ease-out"
              style={{ height: containerHeight }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex items-start"
                style={getTransformStyle()}
              >
                {reviews.map((review, index) => renderReviewCard(review, index))}
              </div>
            </div>

            {/* Dot Indicators */}
            {reviews.length > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {reviews.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-primary-green w-6'
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to review ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* CTA to view all reviews */}
          <div className="text-center mt-8">
            <a
              href={trustpilotUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary-green font-semibold hover:text-primary-green-dark transition-colors"
            >
              Read all reviews on Trustpilot
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default TrustpilotReviewComponent
